/**
 * World renderer.
 *
 * The world is drawn at an **integer** pixel scale with image smoothing off, so
 * every art pixel is a crisp square on screen no matter the viewport size or
 * camera zoom. UI and HUD are drawn afterwards at native resolution.
 */

import { clamp } from '@shared/math.js';
import type { Camera } from '../core/camera.js';
import { drawCar } from './cars.js';
import type { ParticleSystem, SkidLayer } from './particles.js';
import type { BakedTrack } from './track-art.js';
import type { CarClass } from '@shared/cars.js';

export interface CarDraw {
  x: number;
  y: number;
  angle: number;
  length: number;
  width: number;
  sprite: CarClass | string;
  color: string;
  isPlayer: boolean;
  label?: string;
  boosting?: boolean;
  eliminated?: boolean;
  finishing?: boolean;
}

export interface WorldScene {
  baked: BakedTrack;
  skids: SkidLayer | null;
  particles: ParticleSystem;
  camera: Camera;
  cars: CarDraw[];
  /** Optional racing-line ribbon (practice/assist). */
  racingLine: Array<{ x: number; y: number }> | null;
  /** Optional checkpoint gates (practice/assist). */
  checkpoints: Array<{ x: number; y: number; angle: number; halfWidth: number }> | null;
  /** Draw every car's collision hull — useful when tuning handling. */
  debugHulls?: boolean;
}

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;

  /** CSS pixel size of the viewport. */
  viewWidth = 0;
  viewHeight = 0;
  /** Device pixel ratio actually used (capped to keep fill-rate sane). */
  dpr = 1;
  /** Integer world-to-screen scale. */
  pixelScale = 3;
  /** Visible world size in world pixels. */
  worldViewWidth = 0;
  worldViewHeight = 0;

  /** Extra scale applied on top of the integer scale (1x or 2x only). */
  private baseScale = 3;
  /** Reused so the per-frame transform does not allocate. */
  private readonly shakeScratch = { x: 0, y: 0 };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
    this.resize();
  }

  resize(desiredWorldWidth = 340): void {
    const rect = this.canvas.getBoundingClientRect();
    // Fall back to the window only when the canvas has not been laid out yet,
    // and never clamp to an arbitrary minimum: `viewWidth` must always match the
    // element's real CSS size, because that is the space the UI hit-tests in.
    const cssWidth = Math.max(1, Math.round(rect.width) || Math.round(window.innerWidth) || 960);
    const cssHeight = Math.max(1, Math.round(rect.height) || Math.round(window.innerHeight) || 540);
    const dpr = clamp(window.devicePixelRatio || 1, 1, 2);

    this.viewWidth = cssWidth;
    this.viewHeight = cssHeight;
    this.dpr = dpr;
    // Assigning `width`/`height` clears the canvas even when the value is
    // unchanged, and `applySettings()` calls this from inside a draw pass — so an
    // unconditional assignment here wiped everything already painted before the
    // control that triggered it, which showed up as the screen flickering while a
    // slider was dragged. Only touch the backing store when it really changed.
    const backingWidth = Math.round(cssWidth * dpr);
    const backingHeight = Math.round(cssHeight * dpr);
    if (this.canvas.width !== backingWidth) this.canvas.width = backingWidth;
    if (this.canvas.height !== backingHeight) this.canvas.height = backingHeight;

    // Choose the largest integer scale that still shows `desiredWorldWidth`
    // world pixels across; clamped so very wide screens do not zoom in absurdly.
    const raw = cssWidth / desiredWorldWidth;
    this.baseScale = clamp(Math.round(raw), 1, 6);
    this.pixelScale = this.baseScale;
    this.worldViewWidth = cssWidth / this.pixelScale;
    this.worldViewHeight = cssHeight / this.pixelScale;
  }

  /** True when the canvas's CSS box no longer matches the size we render for. */
  sizeIsStale(): boolean {
    const rect = this.canvas.getBoundingClientRect();
    return Math.round(rect.width) !== this.viewWidth || Math.round(rect.height) !== this.viewHeight;
  }

  /**
   * Pins the base pixel scale (the "pixel size" graphics setting). Values are
   * clamped to whole numbers so the art never gets blurry or uneven.
   */
  setBaseScale(scale: number): void {
    this.baseScale = clamp(Math.round(scale), 1, 8);
    this.pixelScale = this.baseScale;
    this.worldViewWidth = this.viewWidth / this.pixelScale;
    this.worldViewHeight = this.viewHeight / this.pixelScale;
  }

  /** Recomputes the integer scale for a given camera zoom. */
  private applyZoom(zoom: number): void {
    // Zoom is quantised to keep pixels square; the fractional part becomes part
    // of the camera's own smoothing instead.
    const scale = clamp(Math.round(this.baseScale * clamp(zoom, 0.6, 1.6)), 1, 8);
    this.pixelScale = scale;
    this.worldViewWidth = this.viewWidth / scale;
    this.worldViewHeight = this.viewHeight / scale;
  }

  worldToScreen(x: number, y: number, camera: Camera): { x: number; y: number } {
    return {
      x: (x - camera.x) * this.pixelScale + this.viewWidth / 2,
      y: (y - camera.y) * this.pixelScale + this.viewHeight / 2,
    };
  }

  screenToWorld(x: number, y: number, camera: Camera): { x: number; y: number } {
    return {
      x: (x - this.viewWidth / 2) / this.pixelScale + camera.x,
      y: (y - this.viewHeight / 2) / this.pixelScale + camera.y,
    };
  }

  /**
   * Draws the world. Assumes nothing has been drawn yet this frame; the caller
   * draws UI afterwards via `beginScreenSpace`.
   */
  drawWorld(scene: WorldScene): void {
    const camera = scene.camera;
    const ctx = this.ctx;
    this.applyZoom(camera.zoom);

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = '#07080b';
    ctx.fillRect(0, 0, this.viewWidth, this.viewHeight);

    const shake = camera.shakeOffset(this.shakeScratch);
    const originX = -camera.x * this.pixelScale + this.viewWidth / 2 + shake.x * this.pixelScale;
    const originY = -camera.y * this.pixelScale + this.viewHeight / 2 + shake.y * this.pixelScale;

    ctx.save();
    ctx.translate(originX, originY);
    ctx.scale(this.pixelScale, this.pixelScale);

    // Visible world rectangle, used to skip off-screen work.
    const viewLeft = camera.x - this.worldViewWidth / 2 - 40;
    const viewTop = camera.y - this.worldViewHeight / 2 - 40;
    const viewRight = camera.x + this.worldViewWidth / 2 + 40;
    const viewBottom = camera.y + this.worldViewHeight / 2 + 40;

    const baked = scene.baked;
    const srcX = clamp(viewLeft, 0, baked.width);
    const srcY = clamp(viewTop, 0, baked.height);
    const srcW = clamp(viewRight, 0, baked.width) - srcX;
    const srcH = clamp(viewBottom, 0, baked.height) - srcY;
    if (srcW > 0 && srcH > 0) {
      ctx.drawImage(baked.canvas, srcX, srcY, srcW, srcH, srcX, srcY, srcW, srcH);
    }

    if (scene.skids) {
      ctx.drawImage(scene.skids.canvas, srcX, srcY, srcW, srcH, srcX, srcY, srcW, srcH);
    }

    if (scene.racingLine) {
      ctx.save();
      ctx.strokeStyle = 'rgba(120, 220, 255, 0.5)';
      ctx.lineWidth = 3;
      ctx.setLineDash([10, 10]);
      ctx.beginPath();
      scene.racingLine.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.closePath();
      ctx.stroke();
      ctx.restore();
    }

    if (scene.checkpoints) {
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 210, 80, 0.45)';
      ctx.lineWidth = 4;
      for (const gate of scene.checkpoints) {
        const nx = -Math.sin(gate.angle);
        const ny = Math.cos(gate.angle);
        ctx.beginPath();
        ctx.moveTo(gate.x - nx * gate.halfWidth, gate.y - ny * gate.halfWidth);
        ctx.lineTo(gate.x + nx * gate.halfWidth, gate.y + ny * gate.halfWidth);
        ctx.stroke();
      }
      ctx.restore();
    }

    // Cars are sorted by Y so lower cars overlap higher ones, which reads as
    // depth in a top-down view.
    const ordered = [...scene.cars].sort((a, b) => a.y - b.y);
    for (const car of ordered) {
      if (car.x < viewLeft || car.x > viewRight || car.y < viewTop || car.y > viewBottom) continue;
      if (car.eliminated) ctx.globalAlpha = 0.4;
      drawCar(ctx, car.sprite, car.color, car.x, car.y, car.angle, car.length, car.width);
      ctx.globalAlpha = 1;
      if (car.boosting) {
        ctx.save();
        ctx.globalAlpha = 0.55;
        ctx.fillStyle = '#7be6ff';
        const bx = car.x - Math.cos(car.angle) * car.length * 0.6;
        const by = car.y - Math.sin(car.angle) * car.length * 0.6;
        ctx.beginPath();
        ctx.arc(bx, by, car.width * 0.55, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      if (car.isPlayer) {
        // A short ground marker under the player car removes any doubt about
        // which vehicle you are controlling.
        ctx.save();
        ctx.globalAlpha = 0.35;
        ctx.strokeStyle = '#f2c14e';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(car.x, car.y, car.width * 0.95, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
      if (scene.debugHulls) {
        ctx.save();
        ctx.strokeStyle = car.isPlayer ? '#7bffb0' : '#ff8a80';
        ctx.lineWidth = 1;
        ctx.translate(car.x, car.y);
        ctx.rotate(car.angle);
        ctx.strokeRect(-car.length / 2, -car.width / 2, car.length, car.width);
        ctx.restore();
      }
    }

    scene.particles.draw(ctx);
    ctx.restore();
  }

  /** Resets the transform to native-resolution screen space for UI and HUD. */
  beginScreenSpace(): CanvasRenderingContext2D {
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.ctx.imageSmoothingEnabled = true;
    return this.ctx;
  }
}
